'use client';

import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { filtersToUrl, sanitizeFinancialYear, searchParamsWithoutPreview } from '@/lib/search-filters';
import { aiSearchKey } from '@/lib/ai-search';
import { type CompanySearchResponse, searchCompanies } from '@/lib/actions/company-search';
import type { FilterParams } from '@/lib/search-filter-params';
import type { FilterTier } from '@/lib/search-gating';
import { activeFilterValues, hasAnyFilter } from '@/new_components/searchresults/activeFilters';
import type { useRecordRecentSearch } from '@/hooks/use-recent-activity';
import type { useSession } from '@/hooks/use-session';
import { interactionState } from '@/components/page-view-logger';
import { filtersToSearchParams } from './searchPageFilters';
import type { NaceCode } from './searchPageTypes';

type AiOriginRef = RefObject<{ key: string; query: string } | null>;

interface SearchResultsInput {
  initialSearchResponse: CompanySearchResponse;
  renderedSearchParams: string;
  searchParams: URLSearchParams;
  searchSkipped: boolean;
  initialOffset: number;
  initialSort: string;
  initialLimit: number;
  initialFinancialYear: number | undefined;
  initialFilters: FilterParams;
  tier: FilterTier;
  sessionStatus: ReturnType<typeof useSession>['status'];
  recordRecentSearch: ReturnType<typeof useRecordRecentSearch>;
  aiOriginRef: AiOriginRef;
}

/**
 * The results on screen and the page/sort/limit they were fetched with:
 * the state, the default-search fetch on mount, the search log, and the
 * re-sync from SSR when the URL changes externally (in that effect order).
 */
export function useSearchResults({
  initialSearchResponse,
  renderedSearchParams,
  searchParams,
  searchSkipped,
  initialOffset,
  initialSort,
  initialLimit,
  initialFinancialYear,
  initialFilters,
  tier,
  sessionStatus,
  recordRecentSearch,
  aiOriginRef,
}: SearchResultsInput) {
  const [isLoading, setIsLoading] = useState(false);

  // Client-side search state (initialized from SSR response)
  const [searchResponse, setSearchResponse] = useState(initialSearchResponse);
  // The search params the response on screen belongs to. The URL changes
  // before a new response arrives (client search replaces it first; a soft
  // navigation re-renders before the reset effect runs), so the search log
  // must wait until both agree — it used to record the previous count.
  const [responseParams, setResponseParams] = useState(renderedSearchParams);
  const [currentOffset, setCurrentOffset] = useState(initialOffset);
  const [currentSort, setCurrentSort] = useState(initialSort);
  const [currentLimit, setCurrentLimit] = useState(initialLimit);
  const [currentFinancialYear, setCurrentFinancialYear] =
    useState(initialFinancialYear);

  // Fetch default results on mount only when SSR deliberately skipped the
  // search. A real search that returned 0 results must NOT fall back to the
  // default list — it should show "no results".
  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (initialFetchDone.current) return;
    if (searchSkipped) {
      initialFetchDone.current = true;
      setIsLoading(true);
      searchCompanies({ limit: initialLimit, offset: 0, sort: initialSort })
        .then((res) => setSearchResponse(res))
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, [searchSkipped, initialLimit, initialSort]);

  useSearchQueryLog({
    searchParams, renderedSearchParams, responseParams, initialFilters, searchResponse,
    currentSort, sessionStatus, tier, recordRecentSearch, aiOriginRef,
  });

  // Filters behind the currently displayed results. executeSearch records the
  // exact FilterParams it ran with, so the Excel export can re-fetch the same
  // result set (just more rows) regardless of how the search was triggered.
  const lastFiltersRef = useRef<FilterParams>(initialFilters);

  // Sync from SSR when URL changes externally (e.g. browser back/forward, or a
  // new search from the header). Skip only for the default search, whose empty
  // response is a placeholder the on-mount client fetch fills in — for a real
  // search we sync even when it returned 0 results, so the previous results
  // clear instead of lingering.
  //
  // Note: returning to the bare default search from an already-open search page
  // (the "Search" nav link, the header search bar submitted empty) is handled
  // by a full page reload — `hardNavigate` on the link, `goToKeywordlessSearch`
  // in LandingSearch — so this effect doesn't need to untangle stale client
  // state from a same-page soft navigation.
  useEffect(() => {
    if (searchSkipped) return;
    setSearchResponse(initialSearchResponse);
    setResponseParams(renderedSearchParams);
    setCurrentOffset(initialOffset);
    setCurrentSort(initialSort);
    setCurrentLimit(initialLimit);
    setCurrentFinancialYear(initialFinancialYear);
    // The results now on screen were produced by the URL's filters, so the
    // export re-fetch must use those — not whatever executeSearch last ran.
    // initialFilters stays out of the deps on purpose: its identity also
    // changes on every shallow pushState executeSearch performs, and firing
    // there would clobber fresh client results with the stale SSR response.
    lastFiltersRef.current = initialFilters;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchSkipped,
    initialSearchResponse,
    initialOffset,
    initialSort,
    initialLimit,
    initialFinancialYear,
  ]);

  return {
    isLoading, setIsLoading, searchResponse, setSearchResponse, setResponseParams,
    currentOffset, setCurrentOffset, currentSort, setCurrentSort, currentLimit, setCurrentLimit,
    currentFinancialYear, setCurrentFinancialYear, lastFiltersRef,
  };
}

export type SearchResultsState = ReturnType<typeof useSearchResults>;

interface SearchQueryLogInput {
  searchParams: URLSearchParams;
  renderedSearchParams: string;
  responseParams: string;
  initialFilters: FilterParams;
  searchResponse: CompanySearchResponse;
  currentSort: string;
  sessionStatus: ReturnType<typeof useSession>['status'];
  tier: FilterTier;
  recordRecentSearch: ReturnType<typeof useRecordRecentSearch>;
  aiOriginRef: AiOriginRef;
}

/**
 * Log search query to /api/log/search after the result count settles.
 * Gated on real interaction for guests (mirrors PageViewLogger) so crawlers
 * and link unfurlers — which fetch HTML but don't move a mouse — never log.
 * `aiOriginRef` is listed as a dependency only because it arrives as an
 * argument; a ref keeps one identity, so the effect fires exactly as before.
 */
function useSearchQueryLog({
  searchParams,
  renderedSearchParams,
  responseParams,
  initialFilters,
  searchResponse,
  currentSort,
  sessionStatus,
  tier,
  recordRecentSearch,
  aiOriginRef,
}: SearchQueryLogInput) {
  const lastLoggedSearchRef = useRef<string | null>(null);
  useEffect(() => {
    if (sessionStatus === 'loading') return;
    if (typeof navigator !== 'undefined' && navigator.webdriver) return;

    if (!hasAnyFilter(initialFilters, tier)) return;
    if (responseParams !== renderedSearchParams) return;

    const url = `/search?${renderedSearchParams}`;
    if (lastLoggedSearchRef.current === url) return;

    const isGuest = sessionStatus !== 'authenticated';

    const doLog = () => {
      lastLoggedSearchRef.current = url;
      if (sessionStatus === 'authenticated') {
        recordRecentSearch({
          url,
          filters: { ...initialFilters },
          resultCount: searchResponse.total,
        });
      }
      fetch('/api/log/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Every applied filter, derived from the filter object — the old
          // hand-written subset covered 13 of 28, so searches narrowed only
          // by e.g. Rechtsform or membership were invisible to analytics.
          ...activeFilterValues(initialFilters, tier),
          filterKeys: Object.keys(activeFilterValues(initialFilters, tier)),
          sort: currentSort,
          resultCount: searchResponse.total,
          zeroResults: searchResponse.total === 0,
          url,
          ...(aiOriginRef.current?.key ===
            aiSearchKey(new URLSearchParams(renderedSearchParams)) && {
            ai: true,
            aiQuery: aiOriginRef.current.query.slice(0, 200),
          }),
        }),
        keepalive: true,
      }).catch(() => {});
    };

    let timer: ReturnType<typeof setTimeout> | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;

    if (isGuest) {
      timer = setTimeout(() => {
        if (interactionState.happened) {
          doLog();
        } else {
          let checks = 0;
          interval = setInterval(() => {
            checks++;
            if (interactionState.happened) {
              clearInterval(interval!);
              doLog();
            } else if (checks >= 6) {
              clearInterval(interval!);
            }
          }, 2000);
        }
      }, 3000);
    } else {
      doLog();
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (interval) clearInterval(interval);
    };
  }, [
    searchParams,
    renderedSearchParams,
    responseParams,
    initialFilters,
    searchResponse.total,
    currentSort,
    sessionStatus,
    tier,
    recordRecentSearch,
    aiOriginRef,
  ]);
}

/**
 * Client-side search: fetch via API route, update URL without full page
 * reload. The results-state setters and `lastFiltersRef` are listed as
 * dependencies only because they arrive as arguments here; setters and refs
 * are stable, so executeSearch keeps the identity it had before.
 */
export function useExecuteSearch(
  results: SearchResultsState,
  naceCodes: NaceCode[],
  listSlug: string | undefined,
  locale: string,
) {
  const {
    setIsLoading, setSearchResponse, setResponseParams, setCurrentOffset, setCurrentSort,
    setCurrentLimit, setCurrentFinancialYear, lastFiltersRef,
  } = results;
  // Abort controller for in-flight searches
  const abortRef = useRef<AbortController | null>(null);
  // Track whether the first client-side search has happened (for pushState vs replaceState)
  const hasClientSearched = useRef(false);

  return useCallback(
    async (
      filters: FilterParams,
      overrides: {
        sort: string;
        offset: number;
        limit: number;
      },
    ) => {
      // Abort previous in-flight request
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Next patches native history to update useSearchParams, which changes
      // initialFilters and triggers the sidebar sync after a chip removal.
      // Update URL (shallow — no server re-render).
      // Use pushState on first client search so back-button returns to the
      // previous page, then replaceState for subsequent filter changes.
      const url = filtersToUrl(filters, overrides, locale);
      if (!hasClientSearched.current) {
        hasClientSearched.current = true;
        window.history.pushState(null, '', url);
      } else {
        window.history.replaceState(null, '', url);
      }

      // Remember the filters behind this result set for the export re-fetch.
      lastFiltersRef.current = filters;

      setIsLoading(true);
      try {
        const params = filtersToSearchParams(filters, naceCodes, overrides);
        const response = await searchCompanies({
          ...params,
          ...(listSlug && { listSlug, locale }),
        });
        if (!controller.signal.aborted) {
          setSearchResponse(response);
          setResponseParams(
            searchParamsWithoutPreview(
              new URL(url, window.location.origin).searchParams,
            ).toString(),
          );
          setCurrentOffset(overrides.offset);
          setCurrentSort(overrides.sort);
          setCurrentLimit(overrides.limit);
          setCurrentFinancialYear(sanitizeFinancialYear(filters.financialYear));
        }
      } catch (err: unknown) {
        if (!(err instanceof Error && err.name === 'AbortError')) {
          console.error('Search failed:', err);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [
      naceCodes, listSlug, locale,
      setIsLoading, setSearchResponse, setResponseParams, setCurrentOffset, setCurrentSort,
      setCurrentLimit, setCurrentFinancialYear, lastFiltersRef,
    ],
  );
}
